import { z } from 'zod';
import { email, isoDate, uuid } from './common.js';
import { adminDocumentStatus, adminRegistrationStatus } from './admin.js';
import { teamMemberStatus, teamName, teamStatus } from './teams.js';

/**
 * Back-office view of the teams (A-12): list with filters, detail with every seat, the captain
 * appointment, the rename, the replacement or resend of an unpaid seat, moving a paid member to
 * another team of the format, and cancelling or reopening a team (records 0023, 0024). A team
 * whose paid members were all refunded stays `forming` without a captain (record 0021,
 * reviewed 2026-10-28): nobody can invite until the organiser appoints one here. A paid member
 * leaves a team otherwise only through a refund in Stripe.
 */

/**
 * GET /admin/teams filters, applied by the API. `status=incomplete` = `forming` (a cancelled
 * team is not "incomplete"); `captain=none` = no captain appointed; `q` matches the team name,
 * an invited address or a member's own email.
 */
export const adminTeamFilters = z.object({
  q: z.string().trim().max(100).optional(),
  formatId: uuid.optional(),
  status: z.enum(['incomplete', 'complete', 'cancelled']).optional(),
  captain: z.enum(['none']).optional(),
});
export type AdminTeamFilters = z.infer<typeof adminTeamFilters>;
export const adminTeamCaptain = z.object({
  userId: uuid,
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
});

export const adminTeam = z.object({
  id: uuid,
  name: z.string(),
  status: teamStatus,
  formatId: uuid,
  formatName: z.string(),
  /** Null: no paid member left; the organiser appoints one from the paid members. */
  captain: adminTeamCaptain.nullable(),
  sizeMin: z.number().int().positive(),
  /** Null = unlimited. */
  sizeMax: z.number().int().positive().nullable(),
  /** Members who paid their place. */
  joined: z.number().int().nonnegative(),
  /** Invitations not paid yet. */
  invited: z.number().int().nonnegative(),
  createdAt: isoDate,
});
export type AdminTeam = z.infer<typeof adminTeam>;

export const adminTeamsOutput = z.object({
  teams: z.array(adminTeam),
  /** The event's team formats, for the format filter. */
  formats: z.array(z.object({ id: uuid, name: z.string() })),
});
export type AdminTeamsOutput = z.infer<typeof adminTeamsOutput>;

/** Every seat of a team, dead ones included (the history of the roster). */
export const adminTeamMember = z.object({
  id: uuid,
  status: teamMemberStatus,
  /** The member's own email once paid, else the address the captain typed. */
  email: z.string(),
  /** The address the captain typed; differs from `email` when the member paid with another. */
  invitedEmail: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  isCaptain: z.boolean(),
  /**
   * The member's own registration (A-04), once their order was paid: still `paid`, or
   * `refunded` for a refunded seat. Null for a seat nobody paid.
   */
  registration: z
    .object({
      id: uuid,
      status: adminRegistrationStatus,
      /** Their order carries the cancellation insurance (A-13). */
      insured: z.boolean(),
      documentStatus: adminDocumentStatus,
      /**
       * The member's payment in the Stripe dashboard (test or live), where a refund is made
       * (record 0024 Q1); null for an order at 0 € that had nothing to pay.
       */
      stripeUrl: z.string().url().nullable(),
    })
    .nullable(),
  invitedAt: isoDate,
  joinedAt: isoDate.nullable(),
  replacedAt: isoDate.nullable(),
});
export type AdminTeamMember = z.infer<typeof adminTeamMember>;

export const adminTeamDetail = adminTeam.extend({ members: z.array(adminTeamMember) });
export type AdminTeamDetail = z.infer<typeof adminTeamDetail>;

/** POST /admin/teams/:id/captain — a paid member of the team becomes its captain. */
export const setTeamCaptainInput = z.object({ memberId: uuid });
export type SetTeamCaptainInput = z.infer<typeof setTeamCaptainInput>;

/** PATCH /admin/teams/:id — the back office renames a team (record 0021 D22), same rule as checkout. */
export const renameTeamInput = z.object({ name: teamName });
export type RenameTeamInput = z.infer<typeof renameTeamInput>;

/**
 * POST /admin/teams/:id/members/:mid/replace — an unpaid seat goes to another address on
 * request: the old link dies, the new address gets its own link and the "Join your team" email.
 */
export const replaceTeamSeatInput = z.object({ email });
export type ReplaceTeamSeatInput = z.infer<typeof replaceTeamSeatInput>;

/**
 * POST /admin/teams/:id/members/:mid/move — a paid member goes to another team of the same
 * format, keeping their registration and what they paid (record 0024 Q2).
 */
export const moveTeamMemberInput = z.object({ teamId: uuid });
export type MoveTeamMemberInput = z.infer<typeof moveTeamMemberInput>;
